import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Emblem } from "@/components/Logo";
import { Loader, Badge } from "@/components/kit";

export default function SharedMenu() {
  const { token } = useParams();
  const [menu, setMenu] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/shared/menus/${token}`).then(({ data }) => setMenu(data)).catch((e) => setErr(apiErr(e)));
  }, [token]);

  if (err) return (
    <div className="min-h-screen bg-ink flex items-center justify-center p-6">
      <div className="matte-card p-12 text-center max-w-md"><AlertTriangle size={40} className="text-gold mx-auto mb-4" />
        <h1 className="font-serif text-3xl">Menu unavailable</h1><p className="text-white/50 mt-2">{err}</p>
        <Link to="/" className="btn-outline-gold inline-block mt-6 px-6 py-3 text-xs uppercase tracking-widest rounded-sm">Chef Plated</Link></div>
    </div>
  );
  if (!menu) return <div className="min-h-screen bg-ink"><Loader label="Loading menu…" /></div>;

  return (
    <div className="min-h-screen bg-ink grain">
      <header className="glass sticky top-0 z-30 flex items-center justify-between px-5 lg:px-8 h-16">
        <Link to="/" className="flex items-center gap-2.5"><Emblem className="h-8 w-8" /><span className="font-serif text-lg gold-text tracking-wider">CHEF PLATED</span></Link>
        <Badge tone="muted">Read-only preview</Badge>
      </header>
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <p className="label-eyebrow mb-3">{menu.restaurant} · {menu.cuisine} · {menu.service_style}</p>
          <h1 className="font-serif text-5xl md:text-6xl">{menu.menu_name}</h1>
          <p className="text-white/55 mt-4 max-w-2xl mx-auto italic font-serif text-lg">{menu.concept}</p>
        </motion.div>
        <div className="space-y-6">
          {menu.courses.map((c, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="matte-card overflow-hidden">
              {c.plating_image && <div className="h-64 md:h-80 relative"><img src={c.plating_image} alt={c.name} className="w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" /></div>}
              <div className="p-7">
                <p className="label-eyebrow">Course {i + 1} · {c.course}</p>
                <h2 className="font-serif text-3xl mt-1">{c.name}</h2>
                <p className="text-white/60 mt-2">{c.description}</p>
                {c.ingredients?.length > 0 && <p className="text-white/40 text-sm mt-4">{c.ingredients.join(" · ")}</p>}
                {c.allergens?.length > 0 && <p className="text-white/40 text-xs mt-3">Allergens: {c.allergens.join(", ")}</p>}
              </div>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-14 pt-8 border-t border-white/5">
          <Emblem className="h-10 w-10 mx-auto mb-3" />
          <p className="text-white/40 text-sm">Plated with Chef Plated — Imagine It. Plate It. Cost It. Order It.</p>
          <Link to="/" className="btn-outline-gold inline-block mt-5 px-7 py-3 text-xs uppercase tracking-widest rounded-sm">Build your own</Link>
        </div>
      </div>
    </div>
  );
}
